export const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(amount);
};

export const formatDate = (dateString: string): string => {
  return new Intl.DateTimeFormat('en-IN', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(new Date(dateString));
};

export const formatDateTime = (dateString: string): string => {
  return new Intl.DateTimeFormat('en-IN', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    timeZoneName: 'short',
  }).format(new Date(dateString));
};

export const formatDateTimeCompact = (dateString: string): string => {
  return new Intl.DateTimeFormat('en-IN', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  }).format(new Date(dateString));
};

export const formatRelativeTime = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
  
  if (diffInSeconds < 60) {
    return 'Just now';
  }
  
  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) {
    return `${diffInMinutes} minute${diffInMinutes !== 1 ? 's' : ''} ago`;
  }
  
  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) {
    return `${diffInHours} hour${diffInHours !== 1 ? 's' : ''} ago`;
  }
  
  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 7) {
    return `${diffInDays} day${diffInDays !== 1 ? 's' : ''} ago`;
  }
  
  return formatDate(dateString);
};

export const formatTimestamp = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInHours = Math.abs(now.getTime() - date.getTime()) / (1000 * 60 * 60);
  
  // If within 24 hours, show relative time
  if (diffInHours < 24) {
    return formatRelativeTime(dateString);
  }
  
  // Otherwise show formatted date with time
  return formatDateTimeCompact(dateString);
};

// IST-specific formatters (add 5.30 hours)
const addISTOffset = (dateString: string): Date => {
  const date = new Date(dateString);
  // Add 5 hours 30 minutes (330 minutes) to convert to IST
  date.setMinutes(date.getMinutes() + 330);
  return date;
};

export const formatTimestampIST = (dateString: string): string => {
  const istDate = addISTOffset(dateString);
  const now = new Date();
  const diffInHours = Math.abs(now.getTime() - istDate.getTime()) / (1000 * 60 * 60);
  
  // If within 24 hours, show relative time
  if (diffInHours < 24) {
    return formatRelativeTimeIST(dateString);
  }
  
  // Otherwise show formatted date with time
  return formatDateTimeCompactIST(dateString);
};

export const formatRelativeTimeIST = (dateString: string): string => {
  const istDate = addISTOffset(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - istDate.getTime()) / 1000);
  
  if (diffInSeconds < 60) {
    return 'Just now';
  }
  
  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) {
    return `${diffInMinutes} minute${diffInMinutes !== 1 ? 's' : ''} ago`;
  }
  
  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) {
    return `${diffInHours} hour${diffInHours !== 1 ? 's' : ''} ago`;
  }
  
  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 7) {
    return `${diffInDays} day${diffInDays !== 1 ? 's' : ''} ago`;
  }
  
  return formatDateIST(dateString);
};

export const formatDateTimeCompactIST = (dateString: string): string => {
  const istDate = addISTOffset(dateString);
  return new Intl.DateTimeFormat('en-IN', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    timeZone: 'Asia/Kolkata'
  }).format(istDate);
};

export const formatDateIST = (dateString: string): string => {
  const istDate = addISTOffset(dateString);
  return new Intl.DateTimeFormat('en-IN', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'Asia/Kolkata'
  }).format(istDate);
};